import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Link, Paper, Typography } from '@mui/material';
import { providerApi, termsApi } from '../../services/api';
import { formatPrice } from '../../utils/currencyUtils';

// Whole amounts read better as "₪1800" than "₪1800.00"; keep cents when there are some.
const money = (amount, currencyCode) =>
  formatPrice(amount, currencyCode, Number.isInteger(Number(amount)) ? 0 : 2);

/**
 * Overview card for a STORE provider: money still waiting for payout (the held total), when the
 * next payout is due, and the payout policy with a link to the Provider Terms. Self-contained:
 * loads GET /providers/me/payouts/summary and renders loading / error / nothing-held / held. The
 * parent decides visibility (STORE accounts only).
 *
 * The terms link is best effort: the active document URL comes from the same endpoint the terms
 * gate uses; if it cannot be loaded the policy sentence still shows, just without a link.
 */
export default function PayoutsCard() {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [termsUrl, setTermsUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await providerApi.getMyPayoutsSummary();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const terms = await termsApi.getActive(i18n.language);
        if (!cancelled && terms?.url) setTermsUrl(terms.url);
      } catch {
        // Best effort: no link, the policy text stands on its own.
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const hasHeld = Boolean(summary) && Number(summary.heldTotal) > 0;
  const byCurrency = summary?.byCurrency || [];
  const nextDate = summary?.nextReleaseAt ? new Date(summary.nextReleaseAt).toLocaleDateString() : null;

  return (
    <Paper
      elevation={0}
      data-testid="payouts-card"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 3 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled', fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', fontSize: '0.68rem', display: 'block', mb: 1.5,
        }}
      >
        {t('payouts.cardTitle')}
      </Typography>

      {loading && <CircularProgress size={20} aria-label={t('payouts.cardTitle')} />}

      {!loading && failed && (
        <Typography variant="body2" color="text.secondary">{t('payouts.loadFailed')}</Typography>
      )}

      {!loading && !failed && summary && (
        <Box>
          {hasHeld ? (
            <>
              <Typography variant="caption" color="text.secondary">{t('payouts.heldTotalLabel')}</Typography>
              {/* One line per currency when the store has several; otherwise just the total. */}
              {byCurrency.length > 1 ? (
                byCurrency.map((c) => (
                  <Typography key={c.currencyCode} variant="subtitle1" fontWeight={600}>
                    {money(c.amount, c.currencyCode)}
                  </Typography>
                ))
              ) : (
                <Typography variant="h6" fontWeight={700} data-testid="payouts-held-total">
                  {money(summary.heldTotal, summary.currencyCode)}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('payouts.heldOrders', { count: summary.heldOrders })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="payouts-next-release">
                {nextDate ? t('payouts.nextRelease', { date: nextDate }) : t('payouts.nextReleasePending')}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">{t('payouts.none')}</Typography>
          )}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('payouts.policy')}
        {termsUrl && (
          <>
            {' '}
            <Link href={termsUrl} target="_blank" rel="noopener noreferrer" data-testid="payouts-terms-link">
              {t('payouts.termsLink')}
            </Link>
          </>
        )}
      </Typography>
    </Paper>
  );
}
